import type {
  SubmitPhoneNumberReq,
  SubmitPhoneNumberResp,
  VerifyPhoneCodeReq,
  VerifyPhoneCodeResp,
} from "@/types/accounts.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";

/**
 * Hook for submitting a phone number. Always saves the number as unverified.
 * If smsConsent is true, the server also sends a verification code via Twilio.
 */
export const useSubmitPhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SubmitPhoneNumberReq) => {
      const response = await api.post<SubmitPhoneNumberResp>(
        "/api/a/users/me/onboarding/phone/new",
        data,
      );
      return response.data;
    },
    // Awaited, so `mutateAsync` resolves only once the refetch has landed: a
    // number that was already verified sends the caller straight to the setup
    // thread, and that page decides from this cache whether the user still
    // needs onboarding.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });
};

/**
 * Hook for verifying phone verification codes via Twilio Verify
 */
export const useVerifyPhoneCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerifyPhoneCodeReq) => {
      const response = await api.post<VerifyPhoneCodeResp>(
        "/api/a/users/me/onboarding/phone/verify",
        data,
      );
      return response.data;
    },
    // `currentUser` is what the home route reads to decide whether this user
    // still needs onboarding, and verifying is the write that changes the
    // answer. Without the refetch they land on home and get sent straight back.
    // The refetch is awaited, not just started, so `mutateAsync` resolves with
    // the cache already saying "verified" and the caller can navigate on it.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });
};

/**
 * Hook for resending the verification code. No body — the backend resends to
 * the user's pending unverified phone handle, which /phone/new persisted
 * server-side. Decouples resend from any in-memory phone-number state.
 */
export const useResendPhoneCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post<{ success: boolean }>(
        "/api/a/users/me/onboarding/phone/resend",
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });
};
